import React, { useEffect, useRef, useState } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import { FormikProps } from 'formik';

import {
  ErrorMessage,
  FileActions,
  FileItem,
  FileListContainer,
  FileName,
  FileTypeText,
  HiddenInput,
  IconContainer,
  RemoveButton,
  Span,
  UploadBox,
  UploadText,
} from './styles';

interface FileUploadProps {
  name: string;
  formik: FormikProps<any>;
  maxSize?: number;
  acceptedTypes?: string[];
  multiple?: boolean;
  truncateFileNames?: boolean;
  onUploadFile?: (file: any) => void;
  existingFiles?: any[];
  onRemoveFile?: (index: number) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  name,
  formik,
  maxSize = 50,
  acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'],
  multiple = false,
  truncateFileNames = false,
  onUploadFile,
  existingFiles = [],
  onRemoveFile,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (existingFiles && existingFiles.length > 0) {
      const currentValue = formik.values[name];
      if (!currentValue || (Array.isArray(currentValue) && currentValue.length === 0)) {
        formik.setFieldValue(name, multiple ? existingFiles : existingFiles[0]);
      }
    }
  }, [existingFiles, formik, name, multiple]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const processFiles = async (newFiles: File[]) => {
    const validFiles = newFiles.filter((file) => {
      if (!acceptedTypes.includes(file.type)) {
        alert(`Arquivo "${file.name}" não é permitido. Verifique os tipos aceitos.`);
        return false;
      }

      if (file.size > maxSize * 1024 * 1024) {
        alert(`Arquivo "${file.name}" é muito grande. O tamanho máximo é ${maxSize}MB.`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    const filesToAdd = multiple ? validFiles : [validFiles[0]];

    const updatedLocalFiles = multiple ? [...files, ...filesToAdd] : filesToAdd;
    setFiles(updatedLocalFiles);

    if (onUploadFile) {
      try {
        const processedFiles: any = await onUploadFile(filesToAdd);
        if (processedFiles && Array.isArray(processedFiles)) {
          const currentFiles = Array.isArray(formik.values[name]) ? formik.values[name] : [];
          const updatedFormikFiles = multiple
            ? [...currentFiles, ...processedFiles]
            : processedFiles;
          formik.setFieldValue(name, updatedFormikFiles);
        }
      } catch (error) {
      }
    } else {
      const currentFiles = Array.isArray(formik.values[name]) ? formik.values[name] : [];
      const updatedFiles = multiple ? [...currentFiles, ...filesToAdd] : filesToAdd;
      formik.setFieldValue(name, updatedFiles);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      processFiles(droppedFiles);
    }
  };

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      processFiles(selectedFiles);
    }
  };

  const removeFile = (index: number) => {
    const currentFiles = formik.values[name] || [];

    if (onRemoveFile) {
      onRemoveFile(index);
    } else {
      if (Array.isArray(currentFiles)) {
        const updatedFiles = currentFiles.filter((_, i) => i !== index);
        formik.setFieldValue(name, updatedFiles);
      } else {
        formik.setFieldValue(name, null);
      }
    }

    if (fileInputRef.current && currentFiles.length <= 1) {
      fileInputRef.current.value = '';
    }
  };

  const getFileIcon = (file: any) => {
    const fileType = file.type || (file.name && file.name.split('.').pop()?.toLowerCase());

    if (fileType === 'application/pdf' || fileType === 'pdf') {
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 1.5A1.5 1.5 0 0 1 4.5 0h5.586a1.5 1.5 0 0 1 1.061.44l2.914 2.914A1.5 1.5 0 0 1 14.5 4.414V14.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2.5 14.5v-13A1.5 1.5 0 0 1 4 1.5h-1z"
            fill="#FF6B6B"
          />
          <path d="M5 7.5h6v1H5v-1zm0 2h6v1H5v-1zm0 2h4v1H5v-1z" fill="white" />
        </svg>
      );
    }

    const videoTypes = ['video/mp4', 'video/quicktime', 'video/x-ms-wmv', 'video/webm', 'video/x-flv', 'mp4', 'mov', 'wmv', 'webm', 'flv'];
    if (videoTypes.includes(fileType)) {
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3z"
            fill="#9C27B0"
          />
          <path d="M6 5v6l5-3-5-3z" fill="white" />
        </svg>
      );
    }

    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2 3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3zm1 0v10h10V3H3z"
          fill="#4CAF50"
        />
        <path d="M4 5l2 3 1.5-2 2.5 4h-6l0-5z" fill="#4CAF50" />
      </svg>
    );
  };

  const getTotalFiles = () => {
    const formikFiles = formik.values[name] || [];
    return Array.isArray(formikFiles) ? formikFiles : [];
  };

  const totalFiles = getTotalFiles();
  const hasFiles = totalFiles.length > 0;

  const acceptsVideo = acceptedTypes.some(type => type.startsWith('video/'));
  const fileTypesText = acceptsVideo 
    ? `JPG, PNG, PDF, MP4 menor que ${maxSize}MB`
    : `JPG, PNG, PDF menor que ${maxSize}MB`;

  return (
    <>
      <UploadBox
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        style={{ backgroundColor: isDragOver ? 'rgba(255, 0, 0, 0.05)' : 'transparent' }}
      >
        <IconContainer>
          {!hasFiles ? (
            <svg
              width="23"
              height="23"
              viewBox="0 0 23 23"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.15434 13.9426C3.15435 14.2446 3.19955 14.5467 3.30668 14.9055L3.33695 15.0071C3.79125 16.3624 5.0429 17.2727 6.44926 17.2727H19.2022L18.4102 19.8176C18.1861 20.6828 17.3866 21.2698 16.5118 21.2698C16.3411 21.2696 16.171 21.2478 16.0059 21.2043L2.42191 17.5266C1.37394 17.2344 0.749394 16.1372 1.02348 15.0793L3.15434 7.88696V13.9426ZM19.8457 1.73071C21.0563 1.73071 22.0419 2.72666 22.042 3.95044V13.7209C22.0418 14.9447 21.0562 15.9407 19.8457 15.9407H6.66801C5.45791 15.9405 4.47295 14.9446 4.4727 13.7209V3.95044C4.47286 2.72679 5.45786 1.73092 6.66801 1.73071H19.8457ZM6.66801 3.50708C6.42589 3.50729 6.22968 3.70548 6.22953 3.95044V12.3342L8.65434 9.88403C9.25436 9.27663 10.2313 9.27662 10.8321 9.88403L11.8955 10.9553L15.1582 6.99634C15.4499 6.64279 15.877 6.43837 16.3321 6.43579C16.7895 6.44637 17.2155 6.63291 17.5098 6.98169L20.2852 10.2561V3.95044C20.285 3.70537 20.0882 3.50708 19.8457 3.50708H6.66801ZM8.8643 4.39478C9.83333 4.39478 10.6211 5.19148 10.6211 6.17114C10.621 7.15073 9.83328 7.94751 8.8643 7.94751C7.89556 7.94727 7.10755 7.15054 7.10746 6.17114C7.10746 5.19167 7.89547 4.39502 8.8643 4.39478Z"
                fill="#A3A3A3"
              />
            </svg>
          ) : (
            <svg
              width="17"
              height="13"
              viewBox="0 0 17 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.29507 10.162L1.82507 6.69202C1.63809 6.50504 1.38449 6.4 1.12007 6.4C0.855641 6.4 0.602045 6.50504 0.415067 6.69202C0.22809 6.879 0.123047 7.13259 0.123047 7.39702C0.123047 7.52795 0.148836 7.6576 0.198941 7.77856C0.249046 7.89953 0.322486 8.00944 0.415067 8.10202L4.59507 12.282C4.98507 12.672 5.61507 12.672 6.00507 12.282L16.5851 1.70202C16.772 1.51504 16.8771 1.26145 16.8771 0.997021C16.8771 0.732594 16.772 0.478998 16.5851 0.292021C16.3981 0.105043 16.1445 0 15.8801 0C15.6156 0 15.362 0.105043 15.1751 0.292021L5.29507 10.162Z"
                fill="#0043CE"
              />
            </svg>
          )}
        </IconContainer>

        {!hasFiles ? (
          <>
            <UploadText>
              <span style={{ color: '#0055FF', fontWeight: '500' }}>Click para upload</span> ou
              arraste e solte
            </UploadText>
            <FileTypeText>{fileTypesText}</FileTypeText>
          </>
        ) : (
          <FileListContainer>
            {totalFiles.map((file: any, index: number) => (
              <FileItem key={file.uid || file.id || `file-${file.name}-${file.size || index}`}>
                <IconContainer style={{ marginRight: '8px' }}>{getFileIcon(file)}</IconContainer>
                <FileName title={file.name}>
                  {truncateFileNames && file.name.length > 30
                    ? `${file.name.substring(0, 30)}...`
                    : file.name}
                </FileName>
                <FileActions>
                  <RemoveButton
                    onClick={(e) => {
                      e?.stopPropagation();
                      removeFile(index);
                    }}
                  >
                    <CloseIcon />
                  </RemoveButton>
                </FileActions>
              </FileItem>
            ))}
          </FileListContainer>
        )}

        <HiddenInput
          type="file"
          ref={fileInputRef}
          accept={acceptedTypes.join(',')}
          onChange={handleFileChange}
          multiple={multiple}
        />

        <Span>Escolha outro</Span>
      </UploadBox>

      {formik.touched[name] && formik.errors[name] && (
        <ErrorMessage>{formik.errors[name] as string}</ErrorMessage>
      )}
    </>
  );
};

export default FileUpload;

// Alias for backwards compatibility
export { FileUpload as ImageUpload };
